// Centralized site content + SEO metadata for Ridgehawk Logistics.
// Copy is verbatim from the official content package — do not paraphrase.

export const SITE = {
  name: "Ridgehawk Logistics",
  wordmark: "RIDGEHAWK LOGISTICS",
  tagline: "Precision Logistics",
  city: "Milton, Ontario",
  email: "info@ridgehawk.ca",
  domain: "ridgehawk.ca",
  hours: "Monday – Friday, 7:00 AM – 7:00 PM EST",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "Resources", to: "/blog", children: [
    { label: "Blog", to: "/blog" },
    { label: "Case Studies", to: "/case-studies" },
  ]},
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    slug: "same-day-local-courier",
    short: "Same-Day Courier",
    title: "Same-Day Local Courier",
    overviewBody:
      "Fast, reliable same-day pickup and delivery across Milton and the Halton Region. From documents to parcels, your shipment is in trusted hands — every time.",
    subheadline: "Milton · Halton Region · Greater Toronto Area",
    longBody:
      "Need it there today? Ridgehawk offers same-day pickup and delivery across Milton, the Halton Region, and the Greater Toronto Area. Whether it's a time-sensitive document, a critical part, or a client-facing package, we treat every shipment with urgency and care.",
    features: [
      "Same-day pickup and delivery within Milton and Halton Region",
      "Extended coverage across the Greater Toronto Area",
      "Real-time delivery updates",
      "Careful handling of fragile and high-value items",
      "Flexible scheduling — on-demand or pre-booked",
    ],
    cta: "Request a Same-Day Quote",
    image: "/images/service-courier.jpg",
    iconName: "Truck",
  },
  {
    slug: "medical-pharmaceutical-transport",
    short: "Medical & Pharma Transport",
    title: "Medical & Pharmaceutical Transport",
    overviewBody:
      "Certified handling of lab specimens, pharmaceuticals, and medical supplies. WHMIS, TDG, and Bloodborne Pathogens trained — because compliance isn't optional, it's foundational.",
    subheadline: "Certified. Compliant. Trusted.",
    longBody:
      "Medical logistics demands more than speed — it demands precision, compliance, and an unwavering commitment to safety. Ridgehawk is WHMIS, TDG by Ground, and Bloodborne Pathogens certified, making us a trusted partner for clinics, laboratories, pharmacies, and healthcare providers across the region. We transport lab specimens, pharmaceutical products, medical devices, and regulated health materials with the care and documentation they require. Every shipment follows strict chain-of-custody protocols, temperature sensitivity awareness, and regulatory compliance standards.",
    features: [
      "WHMIS, TDG by Ground, and Bloodborne Pathogens certified driver",
      "Lab specimen and sample transport",
      "Pharmaceutical and medical supply delivery",
      "Compliant handling and documentation",
      "Dedicated routes available for recurring medical logistics needs",
      "Discreet, professional, and punctual service",
    ],
    cta: "Discuss Your Medical Transport Needs",
    image: "/images/service-medical.jpg",
    iconName: "ShieldPlus",
  },
  {
    slug: "last-mile-parcel-delivery",
    short: "Last-Mile Delivery",
    title: "Last-Mile Parcel Delivery",
    overviewBody:
      "The final mile matters most. We bridge the gap between distribution centres and doorsteps with precision routing and real-time accountability.",
    subheadline: "The final mile is the one that matters most.",
    longBody:
      "The last mile is where logistics meets your customer. It's the handshake moment — the impression that sticks. Ridgehawk bridges the gap between your distribution centre and your customer's door with precision routing, careful handling, and real-time accountability. Whether you're an e-commerce brand, a retailer, or a distributor, we handle the final leg of delivery with the same care you put into your product.",
    features: [
      "Residential and commercial last-mile delivery",
      "Careful handling — no tossed packages, no missed doorsteps",
      "Real-time delivery confirmation",
      "Flexible volume — from a handful of parcels to high-volume daily runs",
      "Integration-friendly for e-commerce and retail fulfilment",
    ],
    cta: "Get a Last-Mile Quote",
    image: "/images/service-parcel.jpg",
    iconName: "PackageCheck",
  },
  {
    slug: "b2b-freight-cargo",
    short: "B2B Freight & Cargo",
    title: "B2B Freight & Cargo",
    overviewBody:
      "Pallets, commercial goods, and bulk shipments moved with care and efficiency. Dedicated capacity for businesses that demand consistency.",
    subheadline: "Pallets, parts, and commercial goods — moved with care.",
    longBody:
      "Ridgehawk handles business-to-business freight with the reliability your supply chain depends on. From pallets and bulk materials to high-value commercial goods, we provide dedicated van-based cargo transport tailored to your schedule and specifications. Our van-based approach offers advantages over traditional trucking for mid-size shipments: faster loading, more flexible scheduling, and direct point-to-point delivery without hub transfers or consolidation delays.",
    features: [
      "Pallet and bulk goods transport",
      "Direct point-to-point delivery — no hub transfers",
      "Flexible scheduling — daily, weekly, or on-demand",
      "Careful handling of fragile and high-value commercial goods",
      "Competitive pricing for regular volume",
      "TDG certified for regulated cargo",
    ],
    cta: "Request a Freight Quote",
    image: "/images/service-freight.jpg",
    iconName: "Boxes",
  },
  {
    slug: "dedicated-route-contracts",
    short: "Dedicated Routes",
    title: "Dedicated Route Contracts",
    overviewBody:
      "Scheduled, recurring delivery routes tailored to your business. One provider, one relationship, total reliability — day after day.",
    subheadline: "Your route. Your schedule. One trusted partner.",
    longBody:
      "For businesses that need reliable, recurring logistics, Ridgehawk offers dedicated route contracts — scheduled pickup and delivery runs tailored to your operations. One provider, one relationship, total consistency. Dedicated routes eliminate the variability of on-demand booking. You get a committed schedule, a familiar driver, and a partner who understands your business. Whether it's daily inter-office runs, weekly supply deliveries, or standing medical transport routes, we build the schedule around you.",
    features: [
      "Custom route planning tailored to your business",
      "Daily, weekly, or custom frequency",
      "Consistent driver assignment where possible",
      "Priority scheduling and guaranteed capacity",
      "Volume-based pricing for long-term contracts",
      "Ideal for medical facilities, offices, warehouses, and retail chains",
    ],
    cta: "Design Your Dedicated Route",
    image: "/images/service-routes.jpg",
    iconName: "Route",
  },
];

export const TRUST_SIGNALS = [
  {
    iconName: "ShieldCheck",
    title: "Safety Certified",
    body:
      "WHMIS, Transportation of Dangerous Goods (TDG) by Ground, and Bloodborne Pathogens certified. Every shipment meets the highest safety and compliance standards.",
  },
  {
    iconName: "Clock",
    title: "Same-Day Capability",
    body:
      "Local courier service with same-day pickup and delivery across Milton, Halton Region, and the Greater Toronto Area.",
  },
  {
    iconName: "UserCheck",
    title: "Founder-Led Service",
    body:
      "Ridgehawk isn't a call centre — it's a founder-operated business where every client relationship matters. You'll always know who's handling your shipment.",
  },
  {
    iconName: "MapPin",
    title: "Milton-Based, GTA-Ready",
    body:
      "Headquartered in Milton, Ontario, with service coverage spanning the Greater Toronto Area and beyond. Local roots, regional reach.",
  },
];

export const PROMISES = [
  {
    iconName: "Package",
    title: "Every package handled with care.",
    body:
      "From document envelopes to palletized freight, every shipment is treated as if it were the only one in the van.",
  },
  {
    iconName: "Timer",
    title: "Every deadline respected.",
    body:
      "We don't deliver \"approximately.\" We deliver exactly — on time, every time, with the documentation to prove it.",
  },
  {
    iconName: "Handshake",
    title: "Every client treated as a partner.",
    body:
      "Logistics should be personal. You'll always know who's handling your shipment, and you can reach us directly.",
  },
  {
    iconName: "Award",
    title: "Built on a simple belief.",
    body:
      "Logistics should be personal, precise, and premium — not a faceless transaction. That's the Ridgehawk standard.",
  },
];

export const VALUES = [
  {
    iconName: "Crosshair",
    title: "Precision",
    body:
      "Every route is planned. Every deadline is real. We don't deliver \"approximately\" — we deliver exactly.",
  },
  {
    iconName: "UserCheck",
    title: "Accountability",
    body:
      "Founder-led means founder-accountable. When you call Ridgehawk, you reach someone who cares about your shipment as much as you do.",
  },
  {
    iconName: "ShieldCheck",
    title: "Safety",
    body:
      "WHMIS, TDG by Ground, and Bloodborne Pathogens certified. Compliance is the foundation, not the ceiling. We exceed the standard because your cargo deserves it.",
  },
  {
    iconName: "Repeat",
    title: "Consistency",
    body:
      "The best logistics is invisible — it just works, every single time. That's what we build: quiet, reliable, repeatable excellence.",
  },
];

export const CERTIFICATIONS = [
  {
    title: "WHMIS Certified",
    body:
      "Workplace Hazardous Materials Information System — trained in the safe handling, storage, and transport of hazardous materials in compliance with Canadian federal and provincial regulations.",
  },
  {
    title: "TDG by Ground Certified",
    body:
      "Transportation of Dangerous Goods by Ground — certified under the Canadian TDG Act for the safe transport of regulated and dangerous goods by road, including proper classification, documentation, and emergency response.",
  },
  {
    title: "Bloodborne Pathogens Certified",
    body:
      "Trained in the safe handling and transport of materials containing bloodborne pathogens, including lab specimens and medical waste, in compliance with occupational health and safety standards.",
  },
];

export const PRIMARY_AREAS = [
  "Milton",
  "Oakville",
  "Burlington",
  "Georgetown",
  "Acton",
  "Halton Hills",
  "Mississauga",
  "Brampton",
  "Hamilton",
  "Toronto (all boroughs)",
  "Vaughan",
  "Markham",
  "Richmond Hill",
];

export const EXTENDED_AREAS = [
  "Kitchener-Waterloo",
  "Cambridge",
  "Guelph",
  "Niagara Region",
  "London (by arrangement)",
];

export const SEO = {
  home: {
    title: "Ridgehawk Logistics — Premium Courier & Delivery in Milton, Ontario",
    description:
      "Same-day courier, medical transport, last-mile delivery, and B2B freight services in Milton and the Greater Toronto Area. Safety certified. Founder-led.",
    keywords:
      "Milton courier, same-day delivery Milton, logistics Milton Ontario, courier GTA, medical transport Ontario",
    path: "/",
  },
  about: {
    title: "About Ridgehawk Logistics — Our Story, Values & Certifications",
    description:
      "Founded in Milton, Ontario. WHMIS, TDG, and Bloodborne Pathogens certified. Precision logistics with a founder-led difference.",
    keywords:
      "about Ridgehawk, Milton logistics company, certified courier Ontario, founder-led logistics",
    path: "/about",
  },
  services: {
    title:
      "Logistics Services — Courier, Medical Transport, Freight & Dedicated Routes",
    description:
      "Full-service logistics: same-day courier, certified medical transport, last-mile delivery, B2B freight, and dedicated route contracts in Milton and GTA.",
    keywords:
      "courier services Milton, medical transport Ontario, last-mile delivery GTA, B2B freight Halton, dedicated route logistics",
    path: "/services",
  },
  serviceAreas: {
    title: "Service Areas — Milton, Halton Region, Greater Toronto Area",
    description:
      "Ridgehawk Logistics serves Milton, Oakville, Burlington, Mississauga, Brampton, Toronto, and the wider GTA.",
    keywords:
      "courier Milton, delivery Oakville, logistics Burlington, same-day Mississauga, Halton delivery",
    path: "/service-areas",
  },
  contact: {
    title: "Contact Ridgehawk Logistics — Get a Quote",
    description:
      "Request a quote for same-day courier, medical transport, or dedicated route services. We respond within one business hour.",
    keywords: "contact Ridgehawk, quote courier Milton, logistics quote GTA",
    path: "/contact",
  },
};
